import api from "../helpers/axios";
import { extractUserFromToken } from "../helpers/auth";

class FeedbackService {
  /**
   * Get feedback statistics for dashboard
   * @param {string} startDate - ISO date string (optional)
   * @param {string} endDate - ISO date string (optional)
   * @param {number} facilityId - Facility ID for superadmin (optional)
   * @returns {Promise<Object>} Feedback statistics
   */
  async getFeedbackStats(startDate = null, endDate = null, facilityId = null) {
    try {
      const user = extractUserFromToken();
      const params = new URLSearchParams();

      if (startDate) {
        // 🔧 FIX: Send full datetime to include time portion
        params.append("start_date", startDate);
      }
      if (endDate) {
        // 🔧 FIX: Send full datetime to include end-of-day time
        params.append("end_date", endDate);
      }

      // Only superadmins can filter by facility_id
      if (facilityId && user?.role === "superadmin") {
        params.append("facility_id", facilityId);
      }

      const response = await api.get(
        `/admin/feedback/stats?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching feedback stats:", error);
      throw error;
    }
  }

  /**
   * Get paginated feedback list with filtering
   * @param {Object} filters - Filter options
   * @param {string} filters.feedbackType - "thumbs_up" | "thumbs_down" (optional)
   * @param {string} filters.category - Category name (optional)
   * @param {boolean} filters.isAddressed - Filter by addressed status (optional)
   * @param {string} filters.startDate - ISO date string (optional)
   * @param {string} filters.endDate - ISO date string (optional)
   * @param {number} filters.facilityId - Facility ID for superadmin (optional)
   * @param {number} filters.limit - Items per page (default 20)
   * @param {string} filters.cursor - Pagination cursor (optional)
   * @returns {Promise<Object>} Paginated feedback list
   */
  async getFeedbackList(filters = {}) {
    try {
      const user = extractUserFromToken();
      const params = new URLSearchParams();

      // Add filter parameters
      if (filters.feedbackType)
        params.append("feedback_type", filters.feedbackType);
      if (filters.category) params.append("category", filters.category);
      // Multi-select category filter: the API takes one `category`; with several
      // selected the UI narrows the loaded rows itself.
      else if (Array.isArray(filters.categories) && filters.categories.length === 1) {
        params.append("category", filters.categories[0]);
      }
      if (filters.search) params.append("search", filters.search);
      if (filters.isAddressed !== undefined && filters.isAddressed !== null) {
        params.append("is_addressed", filters.isAddressed);
      }
      if (filters.startDate) params.append("start_date", filters.startDate);
      if (filters.endDate) params.append("end_date", filters.endDate);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.cursor) params.append("cursor", filters.cursor);

      // Only superadmins can filter by facility_id
      if (filters.facilityId && user?.role === "superadmin") {
        params.append("facility_id", filters.facilityId);
      }

      const response = await api.get(`/admin/feedback?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching feedback list:", error);
      throw error;
    }
  }

  /**
   * Mark feedback items as addressed (bulk operation)
   * @param {string[]} feedbackIds - Array of feedback IDs
   * @returns {Promise<Object>} Operation result
   */
  async markFeedbackAsAddressed(feedbackIds) {
    try {
      const response = await api.patch("/admin/feedback/mark-addressed", {
        feedback_ids: feedbackIds,
      });
      return response.data;
    } catch (error) {
      console.error("Error marking feedback as addressed:", error);
      throw error;
    }
  }

  async reopenFeedback(feedbackIds) {
    try { 
        const response = await api.patch("/admin/feedback/reopen", {
            feedback_ids: feedbackIds,
        });
        return response.data;
    } catch (error) {
        console.error("Error reopening feedback:", error);
        throw error;
    }
}

  /**
   * Get detailed feedback by IDs (for incident creation)
   * @param {string[]} feedbackIds - Array of feedback IDs
   * @returns {Promise<Array>} Detailed feedback items with chat context
   */
  async getFeedbackDetails(feedbackIds) {
    try {
      const response = await api.post("/admin/feedback/details", {
        feedback_ids: feedbackIds,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching feedback details:", error);
      throw error;
    }
  }

  /**
   * Get active feedback categories (public endpoint)
   * @returns {Promise<Object>} Active categories
   */
  async getFeedbackCategories() {
    try {
      const response = await api.get("/feedback/categories");
      return response.data;
    } catch (error) {
      console.error("Error fetching feedback categories:", error);
      throw error;
    }
  }

  /**
   * Get all feedback categories for admin management
   * @returns {Promise<Object>} All categories (active + inactive)
   */
  async getAllFeedbackCategories() {
    try {
      const response = await api.get("/admin/feedback/categories");
      return response.data;
    } catch (error) {
      console.error("Error fetching all feedback categories:", error);
      console.error(
        "[FeedbackService] getAllFeedbackCategories - Error details:",
        {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
        }
      );
      throw error;
    }
  }

  /**
   * Create new feedback category (superadmin only)
   * @param {Object} categoryData - Category information
   * @param {string} categoryData.name - Internal name (snake_case)
   * @param {string} categoryData.display_name - User-facing name
   * @param {string} categoryData.description - Category description
   * @param {string} categoryData.tag - Grouping tag
   * @param {number} categoryData.display_order - Sort order
   * @returns {Promise<Object>} Created category
   */
  async createFeedbackCategory(categoryData) {
    try {
      const response = await api.post(
        "/admin/feedback/categories",
        categoryData
      );
      return response.data;
    } catch (error) {
      console.error("Error creating feedback category:", error);
      throw error;
    }
  }

  /**
   * Update feedback category (superadmin only)
   * @param {string} categoryId - Category ID
   * @param {Object} updateData - Fields to update
   * @returns {Promise<Object>} Updated category
   */
  async updateFeedbackCategory(categoryId, updateData) {
    try {
      const response = await api.put(
        `/admin/feedback/categories/${categoryId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating feedback category:", error);
      throw error;
    }
  }

  /**
   * Delete feedback category (superadmin only)
   * @param {string} categoryId - Category ID to delete
   * @returns {Promise<void>} Empty response on success
   */
  async deleteFeedbackCategory(categoryId) {
    try {
      const response = await api.delete(
        `/admin/feedback/categories/${categoryId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting feedback category:", error);
      throw error;
    }
  }

  async getLinkedIncidents(feedbackId) {
    try {
      const response = await api.get(`/admin/feedback/${feedbackId}/incidents`);
      return response.data;
    } catch (error) {
      console.error("Error fetching linked incidents:", error);
      throw error;
    }
  }
}

const feedbackService = new FeedbackService();
export default feedbackService;
